import { store } from '../redux';
import { IError, IFormattedMessageProps, IKeyValuePair, ITranslateKeyError, IMessage } from '../interfaces';
import { deleteDocumentsFromList, updateDocument } from '../redux/mailbox/actions';
import { getMessage } from '../services/message.service';
import { ErrorCodes, ErrorContext, NgDpFileTypes } from '../enums';

interface IErrorCodes {
    [code: string]: IErrorCodeDefinition;
}

interface IErrorCodeDefinition {
    defaultKey: string;
    componentClasses?: Array<IErrorCodeComponent>;
}

interface IErrorCodeComponent {
    context: ErrorContext;
    contextDefaultKey: string;
    properties?: IKeyValuePair;
}

export function getErrorTextProps(errors: IError[], defaultErrorText = 'ERROR'): IFormattedMessageProps[] {
    if (!errors) {
        return [];
    }

    return errors.map((error: IError) => ({ id: getErrorTextByCode(error.errorCode, defaultErrorText) }));
}

export function getErrorTextByCode(errorCode: number, defaultErrorText = 'ERROR'): string {
    const contextErrorCode = httpErrorCodes[errorCode];

    return (contextErrorCode && contextErrorCode.defaultKey) || defaultErrorText;
}

export function getTranslateErrorKey(
    id: string,
    errorCode: number,
    propertyName: string,
    context: ErrorContext
): ITranslateKeyError {
    const errorFound = httpErrorCodes[errorCode];
    let errorKey = null;

    if (errorFound) {
        errorKey = errorFound.defaultKey;
        if (errorFound.componentClasses && context) {
            const compClassFound = errorFound.componentClasses.find((errorFound) => {
                return errorFound.context === context;
            });

            if (compClassFound) {
                if (propertyName && compClassFound.properties) {
                    const foundKey = compClassFound.properties[propertyName];
                    if (foundKey) {
                        return {
                            contextId: id,
                            errorCode: errorCode,
                            key: foundKey.toString(),
                            propertyName: propertyName,
                        };
                    }
                } else {
                    if (compClassFound && compClassFound.contextDefaultKey) {
                        return {
                            contextId: id,
                            errorCode: errorCode,
                            key: compClassFound.contextDefaultKey,
                            propertyName: propertyName,
                        };
                    }
                }
            }
        }
    }

    return { contextId: id, errorCode: errorCode, key: errorKey, propertyName: propertyName };
}

export const httpErrorCodes: IErrorCodes = {
    400: {
        defaultKey: 'MAIL_NOT_FOUND_ERROR',
        componentClasses: [
            {
                context: ErrorContext.MAILITEM,
                contextDefaultKey: 'MAIL_NOT_FOUND_ERROR',
                properties: null,
            },
        ],
    },
    403: { defaultKey: 'DELETE_FOLDER_COULD_NOT_BE_DELETED' },
    404: {
        defaultKey: 'MAIL_NOT_FOUND_ERROR',
        componentClasses: [
            {
                context: ErrorContext.MAIL_INFORMATION,
                contextDefaultKey: 'MAIL_NOT_FOUND_ERROR',
                properties: null,
            },
            {
                context: ErrorContext.MAILITEM,
                contextDefaultKey: 'MAIL_NOT_FOUND_ERROR',
                properties: null,
            },
            {
                context: ErrorContext.ADD_NOTE,
                contextDefaultKey: 'MAIL_NOT_FOUND_ERROR',
                properties: null,
            },
            {
                context: ErrorContext.UPDATE_MESSAGE,
                contextDefaultKey: 'MAIL_NOT_FOUND_ERROR',
                properties: null,
            },
        ],
    },
    1001: {
        defaultKey: null,
        componentClasses: [
            {
                context: ErrorContext.USERS_SCREEN,
                contextDefaultKey: 'USER_NOT_FOUND',
                properties: null,
            },
            {
                context: ErrorContext.UPDATE_FOLDER,
                contextDefaultKey: 'FOLDER_NOT_FOUND',
                properties: null,
            },
            {
                context: ErrorContext.DELETE_FOLDER,
                contextDefaultKey: 'FOLDER_NOT_FOUND',
                properties: null,
            },
            {
                context: ErrorContext.UPDATE_FOLDER_MOVE,
                contextDefaultKey: 'FOLDER_NOT_FOUND',
                properties: null,
            },
        ],
    },
    1027: {
        defaultKey: null,
        componentClasses: [
            {
                context: ErrorContext.ORGANIZATION_FORM,
                contextDefaultKey: null,
                properties: {
                    phoneNumber: 'PHONE_NUMBER_IS_REQUIRED',
                    name: 'CONTACT_NAME_IS_REQUIRED',
                    email: 'EMAIL_IS_REQUIRED',
                },
            },
        ],
    },
    2002: {
        defaultKey: null,
        componentClasses: [
            {
                context: ErrorContext.SENDER_FORM,
                contextDefaultKey: 'SENDER_IS_NOT_UNIQUE',
                properties: null,
            },
            {
                context: ErrorContext.UPDATE_FOLDER,
                contextDefaultKey: 'FOLDER_NAME_ALREADY_EXISTS',
                properties: null,
            },
            {
                context: ErrorContext.CREATE_FOLDER,
                contextDefaultKey: 'FOLDER_NAME_ALREADY_EXISTS',
                properties: null,
            },
            {
                context: ErrorContext.UPDATE_FOLDER_MOVE,
                contextDefaultKey: 'FOLDER_NAME_ALREADY_EXISTS',
                properties: null,
            },
            {
                context: ErrorContext.ORGANIZATION_FORM,
                contextDefaultKey: 'SAME_IP_ADDRESS',
                properties: null,
            },
        ],
    },
    2003: { defaultKey: 'BATCH_GENERAL_ERROR' },
    2004: { defaultKey: 'BATCH_ERROR_VERSION_MISMATCH' },
    13001: { defaultKey: 'ERROR_NO_ORGANIZATION_ID' },
    13002: { defaultKey: 'ERROR_NO_LANGUAGE_ID' },
    13003: { defaultKey: 'ERROR_NO_COUNTRY_ID' },
    13004: { defaultKey: 'ERROR_NO_TERMS_ID' },
    13200: { defaultKey: 'INVALID_PASSWORD' },
    13400: { defaultKey: 'LINK_EXPIRED' },
    13502: { defaultKey: 'ACTIVATE_PASSWORD_TOO_MANY_ATTEMPTS' },
    13503: { defaultKey: 'ACTIVATE_PASSWORD_INVALID_ATTEMPT' },
    13520: { defaultKey: 'INVALID_CURRENT_PASSWORD' },
    13530: { defaultKey: 'NEW_PASSWORD_SHOULD_BE_DIFFERENT' },
    // ContactService
    15400: { defaultKey: 'EMAIL_NOT_FOUND' },
    15410: { defaultKey: 'MAIL_ADDRESS_IS_NOT_VALID' },
    15510: { defaultKey: 'NUMBER_ALREADY_EXISTS' },
    15530: { defaultKey: 'ERROR_INVALID_MOBILENUMBER' },
    15620: { defaultKey: 'VERIFICATION_CODE_EXPIRED' },
    15630: { defaultKey: 'SIGNUP_CONFIRM_SMSCODENOTVALID' },
    15635: { defaultKey: 'VERIFICATION_CODE_WRONG' },
    15640: { defaultKey: 'PHONE_IS_ALREADY_VERIFIED' },
    15650: { defaultKey: 'EXCEED_NUMBER_PIN_VERIFICATIONS' },
    15610: {
        defaultKey: null,
        componentClasses: [
            {
                context: ErrorContext.UNVERIFY_EMAIL,
                contextDefaultKey: 'VERIFICATION_CODE_HAS_NOT_BEEN_REQUESTED',
                properties: null,
            },
            {
                context: ErrorContext.VERIFY_MOBILE,
                contextDefaultKey: 'VERIFICATION_CODE_HAS_NOT_BEEN_REQUESTED_MOBILE',
                properties: null,
            },
        ],
    },
    15660: { defaultKey: 'MAX_MAIL_SUBSCRIPTION' },
    15665: { defaultKey: 'MAX_SMS_SUBSCRIPTION' },
    // UserIdentityService
    17040: { defaultKey: 'USERNAME_ALREADY_EXISTS' },
    17051: { defaultKey: 'USER_ALREADY_EXISTS' },
    // MessageService
    18005: getMessageServiceErrorComponent(),
    18301: getMessageServiceErrorComponent(),
    18302: getMessageServiceErrorComponent(),
    18303: getMessageServiceErrorComponent(),
    // MailboxService
    20001: getMailboxServiceErrorComponent(),
    20002: getMailboxServiceErrorComponent(),
    20050: {
        defaultKey: null,
        componentClasses: [
            {
                context: ErrorContext.CREATE_FOLDER,
                contextDefaultKey: 'FOLDER_CREATE_NAME_EMPTY_OR_INVALID',
                properties: null,
            },
            {
                context: ErrorContext.UPDATE_FOLDER,
                contextDefaultKey: 'FOLDER_RENAME_NAME_EMPTY_OR_INVALID',
                properties: null,
            },
        ],
    },
    20100: { defaultKey: 'FOLDER_NAME_TOO_LONG' },
    20402: getMailboxServiceErrorComponent(),
    20501: { defaultKey: 'FOLDER_SAME_FOLDER_SELECTED' },
    20502: getMailboxServiceErrorComponent(),
    20503: getMailboxServiceErrorComponent(),
    20504: getMailboxServiceErrorComponent(),
    20505: { defaultKey: 'DELETE_FOLDER_COULD_NOT_BE_DELETED' },
    20506: { defaultKey: 'FOLDER_LEVELS_TOO_MANY' },
    20507: { defaultKey: 'FOLDER_AMOUNT_TOO_MANY' },
    37005: { defaultKey: 'INVALID_IP_ADDRESS' },
    // MessageForwardService
    23000: { defaultKey: 'FORWARDING_STATUS_FAIL' },
    23010: { defaultKey: 'NEW_RECIPIENT_IS_NOT_SET' },
    23020: { defaultKey: 'FORWARD_COMMENT_TOO_LONG' },
    23030: { defaultKey: 'FORWARD_SENDER_LABEL_TOO_LONG' },
    23040: { defaultKey: 'FORWARD_NOT_ALLOWED' },
    23050: { defaultKey: 'MESSAGE_TOO_BIG' },
    // OrganizationService
    25003: { defaultKey: 'ORG_MEMBER_EMAIL_FORMAT_INCORRECT' },
    25015: { defaultKey: 'USER_EXISTS' },
    25016: { defaultKey: 'EMPLOYEE_EXISTS_IN_ORG' },
    25017: { defaultKey: 'ORG_ISNT_CREATED' },
    25018: { defaultKey: 'ORG_ID_GREATER' },
    25019: { defaultKey: 'USERNAME_SHOUDNT_BE_EMPTY' },
    25020: { defaultKey: 'ORG_MEMBER_CREATE_NOT_ALLOWED' },
    25027: { defaultKey: 'ORG_MEMBER_CERTIFICATE_EMPTY' },
    // QuotaService
    40005: { defaultKey: 'NEW_QUOTA_EXCEEDED' },
    // Document errors
    50000: { defaultKey: 'FILE_TYPES_INVALID' },
    // Upload errors
    60000: { defaultKey: 'FILE_SIZE_INVALID' },
    60001: { defaultKey: 'FILE_NAME_ALREADY_EXIST' },
    60002: { defaultKey: 'FILE_BAD_CONTENT' },
};

const SIZE_IN_MEGABYTE = 70;

export function mapFileUploadErrors(errors: ErrorCodes[]): IFormattedMessageProps[] {
    return errors.map((error) => {
        switch (error) {
            case ErrorCodes.FILE_TYPES_INVALID:
                return { id: 'FILE_TYPES_INVALID', values: { types: NgDpFileTypes.join(', ') } };
            case ErrorCodes.FILE_SIZE_INVALID:
                return { id: 'FILE_SIZE_INVALID', values: { size: SIZE_IN_MEGABYTE } };
            case ErrorCodes.FILE_BAD_CONTENT:
                return { id: 'FILE_BAD_CONTENT' };
            default:
                return { id: 'ERROR' };
        }
    });
}

export function handleStateOnUpdateDelete(documentList: IMessage[], errors: any, language: string) {
    if (errors && errors.length) {
        const errorsToDelete = errors.filter((error: any) => error.contextId !== null && error.errorCode === 404);
        const errorsToUpdate = errors.filter((error: any) => error.contextId !== null && error.errorCode === 2004);
        if (errorsToDelete && errorsToDelete.length > 0) {
            const messagesToDelete = errorsToDelete.map((error: any) => {
                return documentList.find((doc) => doc.id === error.contextId);
            });
            if (messagesToDelete) {
                store.dispatch(deleteDocumentsFromList(messagesToDelete));
            }
        }
        if (errorsToUpdate && errorsToUpdate.length > 0) {
            const messagesToUpdate = errorsToUpdate.map((error: any) => {
                return documentList.find((doc) => doc.id === error.contextId);
            });
            if (messagesToUpdate && messagesToUpdate.length > 0) {
                messagesToUpdate.forEach((document: IMessage) => {
                    getMessage(document.mailboxId, document.id, language)
                        .then((newDocument) => {
                            if (document.folderId !== newDocument.folderId) {
                                store.dispatch(deleteDocumentsFromList([document]));
                            } else {
                                store.dispatch(updateDocument(newDocument));
                            }
                        })
                        .catch((errors: ITranslateKeyError[]) => {
                            if (errors && errors.length > 0) {
                                if (errors[0].errorCode === 404) {
                                    store.dispatch(deleteDocumentsFromList([document]));
                                }
                            }
                        });
                });
            }
        }
    }
}

function getMessageServiceErrorComponent(): IErrorCodeDefinition {
    return {
        defaultKey: null,
        componentClasses: [
            {
                context: ErrorContext.MOVE_MESSAGES,
                contextDefaultKey: 'UNABLE_TO_MOVE_MAILS',
                properties: null,
            },
            {
                context: ErrorContext.TRASH_MESSAGES,
                contextDefaultKey: 'UNABLE_TO_DELETE_MAILS',
                properties: null,
            },
        ],
    };
}

function getMailboxServiceErrorComponent(): IErrorCodeDefinition {
    return {
        defaultKey: null,
        componentClasses: [
            {
                context: ErrorContext.CREATE_FOLDER,
                contextDefaultKey: 'UNABLE_TO_CREATE_FOLDER',
                properties: null,
            },
            {
                context: ErrorContext.UPDATE_FOLDER,
                contextDefaultKey: 'UNABLE_TO_RENAME_FOLDER',
                properties: null,
            },
            {
                context: ErrorContext.UPDATE_FOLDER_MOVE,
                contextDefaultKey: 'UNABLE_TO_MOVE_FOLDER',
                properties: null,
            },
        ],
    };
}
